import Axios from 'axios';
import { getLocalStorageItem } from '../utils/Localstorage/localStorageUtils';
import { installReauthInterceptor } from '../utils/reauth';
import { saveDraft } from '../utils/drafts';
import { installApiCredentials } from '../utils/apiCredentials';

// In-flight GET requests that were not given their own AbortSignal. They are
// aborted when the user changes page/tab so stale calls don't keep running.
const pendingControllers = new Set();
let initialised = false;

export const abortPendingRequests = () => {
    pendingControllers.forEach((controller) => controller.abort());
    pendingControllers.clear();
};

const initialiseInterceptor = () => {
    if (typeof window !== 'undefined' && !initialised) {
        initialised = true;
        // Our API gets the HttpOnly session cookie (Phase 8).
        installApiCredentials(Axios, process.env.REACT_APP_BACKEND_URL);
        // Critical operations (delete user, change access…): "Confirm it's you"
        // password dialog, then the request is retried.
        installReauthInterceptor(Axios, {
            baseUrl: String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, ''),
            getToken: () => localStorage.getItem('access_token'),
            getCompanyId: () => getLocalStorageItem('company-id'),
            onLocked: () => {
                localStorage.removeItem('access_token');
                if (window.parent !== window) window.parent.postMessage({ type: 'FORCE_LOGOUT' }, '*');
                else window.location.replace('/login');
            },
        });
        Axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token');
                const companyId = getLocalStorageItem('company-id');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                if (companyId) {
                    config.headers['company-id'] = companyId;
                }

                // Only GETs are auto-aborted; aborting a save/update mid-flight is unsafe.
                const isGet = (config.method || 'get').toLowerCase() === 'get';
                if (isGet && !config.signal && !config.cancelToken && !config.keepAlive) {
                    const controller = new AbortController();
                    config.signal = controller.signal;
                    config.__controller = controller;
                    pendingControllers.add(controller);
                }
                return config;
            },
            (error) => Promise.reject(error),
        );

        Axios.interceptors.response.use(
            (response) => {
                pendingControllers.delete(response?.config?.__controller);
                return response;
            },
            (error) => {
                pendingControllers.delete(error?.config?.__controller);

                // Aborted on navigation: the caller is gone, so swallow it (no toast,
                // no setState on unmounted components) by never settling.
                if (Axios.isCancel(error) && error?.config?.__controller) {
                    return new Promise(() => {});
                }

                const status = error?.response?.status;

                if (status === 401 || status === 403) {
                    // Inside BU: a request sent with an old token (before BU handed over its
                    // fresh login, or replaced since) says nothing about the current session —
                    // don't log the whole app out for it.
                    if (window.parent !== window) {
                        const sentWith = String(error?.config?.headers?.Authorization || '');
                        const current = localStorage.getItem('access_token');
                        if (!window.__buParentAuthAt || (current && sentWith && sentWith !== `Bearer ${current}`)) {
                            return Promise.reject(error);
                        }
                    }
                    saveDraft('server'); // keep what the user typed (restored after logging in again)
                    localStorage.removeItem('access_token');

                    if (window.parent !== window) {
                        window.parent.postMessage({ type: 'FORCE_LOGOUT' }, '*');
                    } else {
                    }

                    return Promise.reject(error);
                }

                return Promise.reject(error);
            },
        );
    }
};

export default initialiseInterceptor;
